import * as React from 'react';
import { ChangeEvent, FC, useEffect, useRef } from 'react';

import noop from 'utils/noop';

import { StyledNumberInput } from './styled';
import { INumberInputProps } from './types';

const NUMBER_REGEXP = new RegExp(/^[0-9]*$/);
const NUMBER_INPUT_EXCLUSION_REGEXP = new RegExp(/^(\.|-|e|E)$/);

/**
 * This filter is required because a number input will accept
 * NUMBER_INPUT_EXCLUSION_REGEXP characters before triggering
 * an on change event.
 */
const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
  if (NUMBER_INPUT_EXCLUSION_REGEXP.test(e.key)) {
    e.preventDefault();
  }
};

/**
 * An input that only allows numbers.
 */
export const NumberInput: FC<INumberInputProps> = ({
  min,
  max,
  value = '',
  onChange = noop,
  isContentWidth = false,
  ...props
}) => {
  const oldValue = useRef(value?.toString() || '');

  const handleChange = (e: ChangeEvent<HTMLInputElement>) => {
    const targetValue = e.target.value;
    let newValue = targetValue === '' ? targetValue : oldValue.current;

    if (targetValue !== '' && NUMBER_REGEXP.test(targetValue)) {
      const valueAsNumber = parseInt(targetValue);

      if (typeof min === 'number' && valueAsNumber < min) {
        newValue = min.toString();
      } else if (typeof max === 'number' && valueAsNumber > max) {
        newValue = max.toString();
      } else {
        newValue = valueAsNumber.toString();
      }
    }
    e.target.value = newValue;

    onChange(e);
  };

  useEffect(() => {
    oldValue.current = value?.toString() || '';
  }, [value]);

  return (
    <StyledNumberInput
      {...props}
      type="number"
      value={value?.toString() || ''}
      onChange={handleChange}
      onKeyDown={handleKeyDown}
      isContentWidth={isContentWidth}
      characterCount={
        (isContentWidth && value?.toString() !== 'NaN' && value?.toString()?.length) || 1
      }
    />
  );
};

export default NumberInput;
